import { useState, useCallback } from 'react';
import { useAlert } from '../../../contexts/AlertContext';
import { useLoader } from '../../../contexts/LoaderContext';
import { leaderboardEvents } from '../../../api/leaderboard-events';

interface UseAddCaseFormProps {
  eventId: number;
  participantId?: number;
  onSubmit: (data: any) => void;
  onClose: () => void;
}

export const useAddCaseForm = ({
  eventId,
  participantId,
  onSubmit,
  onClose,
}: UseAddCaseFormProps) => {
  const [formData, setFormData] = useState({
    caseHours: '',
    clientId: '',
    clientFirstName: '',
    clientLastName: '',
    caregiverName: '',
    socDate: null,
    referralDate: null,
    agency: '',
    office: '',
  });

  const { showAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectChange = useCallback((name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDateChange = useCallback((name: string, date: Date | null) => {
    setFormData((prev) => ({ ...prev, [name]: date }));
  }, []);

  const handleSave = async () => {
    try {
      showLoader();
      const payload = {
        hours: Number(formData.caseHours) || 0,
        clientId: formData.clientId ? Number(formData.clientId) : null,
        firstName: formData.clientFirstName || null,
        lastName: formData.clientLastName || null,
        caregiverName: formData.caregiverName || null,
        startOfCareDate: formData.socDate
          ? new Date(formData.socDate as unknown as Date).toISOString()
          : null,
        referralDate: formData.referralDate
          ? new Date(formData.referralDate as unknown as Date).toISOString()
          : null,
        agencyId: formData.agency ? Number(formData.agency) : null,
        officeId: formData.office ? Number(formData.office) : null,
      };

      if (!participantId) throw new Error('Participant is not selected');
      await leaderboardEvents.createClient(eventId, participantId, payload);
      showAlert('Client successfully added', 'success');
      onSubmit(payload);
      onClose();
    } catch (e: any) {
      showAlert(e.message || 'Failed to add client', 'error');
    } finally {
      hideLoader();
    }
  };

  return {
    formData,
    handleChange,
    handleSelectChange,
    handleDateChange,
    handleSave,
  };
};
